'use client';

import { createStore } from 'zustand/vanilla';
import { graphqlRequest } from '@/api/amplify/helpers';
import { accountSettingsByAppKey } from '@/graphql/queries';
import { getAuthenticatedUserData } from '@/api/amplify/helpers';
import { useUserDataStore } from '@/stores/userDataStore';

const TOLSTOY_EMAIL_DOMAIN = 'gotolstoy.com';

export type UserState = {
  account: { [key: string]: any };
  loading: boolean;
};

export type UserActions = {
  fetchAccount: ({ appKey }: { appKey: any }) => Promise<any>;
};

export type UserStore = UserState & UserActions;

export const fetchAccount = async ({ appKey }) => {
  const [accountResponse, userData] = await Promise.all([
    graphqlRequest({ query: accountSettingsByAppKey, variables: { appKey } }),
    getAuthenticatedUserData(),
  ]);

  if (!accountResponse.data.accountSettingsByAppKey.items[0]) {
    return {};
  }

  const { email, name } = userData?.attributes || {};
  return {
    ...accountResponse.data.accountSettingsByAppKey.items[0],
    email,
    name,
  };
};

export const initUserStore = async (): Promise<UserState> => {
  const { appKey, appUrl, isLoggedIn } = useUserDataStore.getState();

  if (!appKey || !isLoggedIn) {
    return defaultInitState;
  }

  const accountData = await fetchAccount({ appKey });
  const isAdmin = accountData?.email?.includes(TOLSTOY_EMAIL_DOMAIN);
  const account = { ...accountData, appUrl, isAdmin };

  return { account, loading: false };
};

export const defaultInitState: UserState = {
  account: {},
  loading: true,
};

export const createUserStore = (initState: Promise<UserState>) => {
  const store = createStore<UserStore>()((set) => ({
    ...defaultInitState,
    fetchAccount,
  }));
  initState.then(store.setState);
  return store;
};
